import React, { useCallback, useEffect, useState } from "react";
import { salaryEnums } from "../../../Constant/HR/staffSalary";
import BaseInput from "../../../BaseComponents/BaseInput";
import { Submit } from "../../../Constant";
import BaseButton from "../../../BaseComponents/BaseButton";
import { useFormik } from "formik";
import * as yup from "yup";
import { validationMessages } from "../../../Constant/validation";
import {
  editSalaryStructureApi,
  effectiveCycleApi,
  viewSalaryStructureApi,
} from "../../../Api/salaryApi";
import { checkStatusCodeSuccess, handleEditClick, RupeesSymbol } from "../../../Constant/common";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { salaryStructureTitle } from "../../../Constant/title";
import { usePageLoader } from "../../../_helper/loading/LoadingContext";
import BackButton from "../../../BaseComponents/BackButton";
import { DollarSign } from "react-feather";

/* ── Shared table row ── */
const SalaryRow = ({
  label,
  calc,
  monthlyField,
  yearlyField,
  formik,
  onMonthlyChange,
  onYearlyChange,
}) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "1fr 80px 1fr 1fr",
      gap: 16,
      padding: "12px 0",
      borderBottom: "1px solid var(--glass-border)",
      alignItems: "start",
    }}
  >
    <span style={{ fontSize: "0.875rem", color: "var(--text-primary)", paddingTop: 10 }}>
      {label}
    </span>
    <span
      style={{
        fontSize: "0.8125rem",
        color: "var(--text-muted)",
        textAlign: "center",
        paddingTop: 10,
      }}
    >
      {calc}
    </span>
    <BaseInput
      name={monthlyField}
      type="text"
      value={formik.values[monthlyField]}
      touched={formik.touched[monthlyField]}
      error={formik.errors[monthlyField]}
      handleBlur={formik.handleBlur}
      handleChange={onMonthlyChange}
    />
    <BaseInput
      name={yearlyField}
      type="text"
      value={formik.values[yearlyField]}
      touched={formik.touched[yearlyField]}
      error={formik.errors[yearlyField]}
      handleBlur={formik.handleBlur}
      handleChange={onYearlyChange}
    />
  </div>
);

const EditSalary = () => {
  document.title = salaryStructureTitle;
  const { staffId } = useParams();
  const navigate = useNavigate();
  const { setLoading } = usePageLoader();
  const [btnLoader, setBtnLoader] = useState(false);
  const [salaryStructure, setSalaryStructure] = useState(null);
  const [previousSalary, setPreviousSalary] = useState(null);
  const [currentEffectiveCycle, setCurrentEffectiveCycle] = useState(null);

  const fetchData = useCallback(() => {
    setLoading(true);
    Promise.all([effectiveCycleApi(staffId), viewSalaryStructureApi(staffId)])
      .then(([cycleResp, structureResp]) => {
        if (checkStatusCodeSuccess(cycleResp.statusCode))
          setCurrentEffectiveCycle(cycleResp?.data?.shift());
        else toast.error(cycleResp?.message);
        if (checkStatusCodeSuccess(structureResp.statusCode)) {
          setSalaryStructure(structureResp?.data);
          setPreviousSalary(structureResp?.data?.monthly_salary);
        } else toast.error(structureResp?.message);
      })
      .catch((err) => toast.error(err?.response?.data?.message ?? err?.message))
      .finally(() => setLoading(false));
  }, [staffId, setLoading]);

  useEffect(() => {
    fetchData();
    handleEditClick();
  }, [fetchData]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      allowance_monthly: salaryStructure?.monthly_special_allowance,
      allowance_yearly: salaryStructure?.yearly_special_allowance,
      basic_monthly: salaryStructure?.monthly_salary,
      basic_yearly: salaryStructure?.yearly_salary,
    },
    validationSchema: yup.object({
      basic_monthly: yup
        .string()
        .required(validationMessages.required(salaryEnums.MONTHLY_BASIC_PAY)),
      basic_yearly: yup
        .string()
        .required(validationMessages.required(salaryEnums.YEARLY_BASIC_PAY)),
    }),
    onSubmit: (values) => {
      setBtnLoader(true);
      editSalaryStructureApi(staffId, {
        monthly_special_allowance: Number(values.allowance_monthly),
        yearly_special_allowance: Number(values.allowance_yearly),
        monthly_salary: Number(values.basic_monthly),
        yearly_salary: Number(values.basic_yearly),
        total_monthly_ctc: monthlyCTC,
        total_yearly_ctc: yearlyCTC,
      })
        .then((resp) => {
          if (checkStatusCodeSuccess(resp.statusCode)) {
            toast.success(resp?.message);
            formik.resetForm();
            navigate(`/salaryStructure/${staffId}`);
          } else toast.error(resp?.message);
        })
        .catch((err) => toast.error(err?.response?.data?.message ?? err?.message))
        .finally(() => setBtnLoader(false));
    },
  });

  const monthlyCTC =
    Number(formik.values.basic_monthly || 0) + Number(formik.values.allowance_monthly || 0);
  const yearlyCTC =
    Number(formik.values.basic_yearly || 0) + Number(formik.values.allowance_yearly || 0);

  const calcYearly = (e, f1, f2) => {
    const val = e.target.value;
    formik.setFieldValue(f1, val);
    formik.setFieldValue(f2, val === "" ? "" : Number(val) * 12);
  };
  const calcMonthly = (e, f1, f2) => {
    const val = e.target.value;
    formik.setFieldValue(f1, val);
    formik.setFieldValue(f2, val === "" ? "" : Number(val) / 12);
  };

  return (
    <div className="animate-fade-in" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Header */}
      <div
        className="animate-slide-down"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h1
            style={{
              fontSize: "1.375rem",
              fontWeight: 800,
              margin: 0,
              color: "var(--text-primary)",
            }}
          >
            {salaryEnums.EDIT_SALARY}
          </h1>
          <p style={{ margin: 0, fontSize: "0.8125rem", color: "var(--text-muted)", marginTop: 2 }}>
            Update salary components for this staff member
          </p>
        </div>
        <BackButton onClick={() => navigate(`/salaryStructure/${staffId}`)} />
      </div>

      <form
        onSubmit={formik.handleSubmit}
        style={{ display: "flex", flexDirection: "column", gap: 16 }}
      >
        {/* Current CTC info */}
        <div
          className="animate-slide-up"
          style={{
            background: "var(--glass-bg)",
            border: "1px solid var(--glass-border)",
            borderRadius: 16,
            overflow: "hidden",
          }}
        >
          <div style={{ height: 3, background: "linear-gradient(90deg,#10b981,#06b6d4)" }} />
          <div style={{ padding: "20px 24px", display: "flex", flexWrap: "wrap", gap: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  background: "rgba(16,185,129,0.12)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <DollarSign size={18} strokeWidth={2} style={{ color: "#10b981" }} />
              </div>
              <div>
                <div
                  style={{
                    fontSize: "0.6875rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    color: "var(--text-muted)",
                  }}
                >
                  {salaryEnums.EFFECTIVE_CYCLE}
                </div>
                <div
                  style={{ fontSize: "0.9375rem", fontWeight: 700, color: "var(--text-primary)" }}
                >
                  {currentEffectiveCycle || "—"}
                </div>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  background: "rgba(99,102,241,0.12)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <DollarSign size={18} strokeWidth={2} style={{ color: "var(--brand-indigo)" }} />
              </div>
              <div>
                <div
                  style={{
                    fontSize: "0.6875rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    color: "var(--text-muted)",
                  }}
                >
                  {salaryEnums.EMPLOYEE_SALARY_CTC}
                </div>
                <div
                  style={{ fontSize: "0.9375rem", fontWeight: 700, color: "var(--text-primary)" }}
                >
                  {previousSalary ? RupeesSymbol(previousSalary) : "—"}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Salary table */}
        <div
          className="animate-slide-up"
          style={{
            animationDelay: "60ms",
            background: "var(--glass-bg)",
            border: "1px solid var(--glass-border)",
            borderRadius: 16,
            overflow: "hidden",
          }}
        >
          <div style={{ height: 3, background: "linear-gradient(90deg,#6366f1,#8b5cf6)" }} />
          <div style={{ padding: "20px 24px" }}>
            {/* Column headers */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 80px 1fr 1fr",
                gap: 16,
                paddingBottom: 10,
                borderBottom: "2px solid var(--glass-border)",
                marginBottom: 4,
              }}
            >
              {[
                salaryEnums.COMPONENTS,
                salaryEnums.CALCULATION,
                salaryEnums.MONTHLY_AMOUNT,
                salaryEnums.YEARLY_AMOUNT,
              ].map((h) => (
                <span
                  key={h}
                  style={{
                    fontSize: "0.6875rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    color: "var(--text-muted)",
                  }}
                >
                  {h}
                </span>
              ))}
            </div>

            {/* Section label */}
            <div
              style={{
                padding: "10px 0",
                color: "var(--brand-indigo)",
                fontSize: "0.8125rem",
                fontWeight: 700,
              }}
            >
              {salaryEnums.FIXED_EARNINGS}
            </div>

            <SalaryRow
              label={salaryEnums.SYSTEM_BASIC}
              calc={salaryEnums.FIXED}
              monthlyField="basic_monthly"
              yearlyField="basic_yearly"
              formik={formik}
              onMonthlyChange={(e) => calcYearly(e, "basic_monthly", "basic_yearly")}
              onYearlyChange={(e) => calcMonthly(e, "basic_yearly", "basic_monthly")}
            />
            <SalaryRow
              label={salaryEnums.SPECIAL_ALLOWANCE}
              calc={salaryEnums.FIXED}
              monthlyField="allowance_monthly"
              yearlyField="allowance_yearly"
              formik={formik}
              onMonthlyChange={(e) => calcYearly(e, "allowance_monthly", "allowance_yearly")}
              onYearlyChange={(e) => calcMonthly(e, "allowance_yearly", "allowance_monthly")}
            />

            {/* CTC total */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 80px 1fr 1fr",
                gap: 16,
                padding: "14px 0",
                marginTop: 4,
                borderTop: "2px solid rgba(99,102,241,0.15)",
                background: "rgba(99,102,241,0.04)",
                borderRadius: 8,
              }}
            >
              <span
                style={{
                  fontSize: "0.9375rem",
                  fontWeight: 800,
                  color: "var(--text-primary)",
                  paddingLeft: 8,
                }}
              >
                {salaryEnums.CTC}
              </span>
              <span />
              <span
                style={{ fontSize: "0.9375rem", fontWeight: 800, color: "var(--brand-indigo)" }}
              >
                {RupeesSymbol(monthlyCTC)}
              </span>
              <span
                style={{ fontSize: "0.9375rem", fontWeight: 800, color: "var(--brand-indigo)" }}
              >
                {RupeesSymbol(yearlyCTC)}
              </span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, paddingBottom: 8 }}>
          <BaseButton
            color="cancel"
            type="button"
            onClick={() => navigate(`/salaryStructure/${staffId}`)}
          >
            Cancel
          </BaseButton>
          <BaseButton color="primary" type="submit" loader={btnLoader} disabled={btnLoader}>
            {Submit}
          </BaseButton>
        </div>
      </form>
    </div>
  );
};

export default EditSalary;
